import React from 'react'
import PropTypes from 'prop-types'

import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import SelectInput from '../SelectInput/SelectInput'

const STATES = [
  { label: '', value: '' },
  { label: 'AL', value: 'AL' },
  { label: 'AK', value: 'AK' },
  { label: 'AS', value: 'AS' },
  { label: 'AZ', value: 'AZ' },
  { label: 'AR', value: 'AR' },
  { label: 'CA', value: 'CA' },
  { label: 'CO', value: 'CO' },
  { label: 'CT', value: 'CT' },
  { label: 'DE', value: 'DE' },
  { label: 'DC', value: 'DC' },
  { label: 'FM', value: 'FM' },
  { label: 'FL', value: 'FL' },
  { label: 'GA', value: 'GA' },
  { label: 'GU', value: 'GU' },
  { label: 'HI', value: 'HI' },
  { label: 'ID', value: 'ID' },
  { label: 'IL', value: 'IL' },
  { label: 'IN', value: 'IN' },
  { label: 'IA', value: 'IA' },
  { label: 'KS', value: 'KS' },
  { label: 'KY', value: 'KY' },
  { label: 'LA', value: 'LA' },
  { label: 'ME', value: 'ME' },
  { label: 'MH', value: 'MH' },
  { label: 'MD', value: 'MD' },
  { label: 'MA', value: 'MA' },
  { label: 'MI', value: 'MI' },
  { label: 'MN', value: 'MN' },
  { label: 'MS', value: 'MS' },
  { label: 'MO', value: 'MO' },
  { label: 'MT', value: 'MT' },
  { label: 'NE', value: 'NE' },
  { label: 'NV', value: 'NV' },
  { label: 'NH', value: 'NH' },
  { label: 'NJ', value: 'NJ' },
  { label: 'NM', value: 'NM' },
  { label: 'NY', value: 'NY' },
  { label: 'NC', value: 'NC' },
  { label: 'ND', value: 'ND' },
  { label: 'MP', value: 'MP' },
  { label: 'OH', value: 'OH' },
  { label: 'OK', value: 'OK' },
  { label: 'OR', value: 'OR' },
  { label: 'PW', value: 'PW' },
  { label: 'PA', value: 'PA' },
  { label: 'PR', value: 'PR' },
  { label: 'RI', value: 'RI' },
  { label: 'SC', value: 'SC' },
  { label: 'SD', value: 'SD' },
  { label: 'TN', value: 'TN' },
  { label: 'TX', value: 'TX' },
  { label: 'UT', value: 'UT' },
  { label: 'VT', value: 'VT' },
  { label: 'VI', value: 'VI' },
  { label: 'VA', value: 'VA' },
  { label: 'WA', value: 'WA' },
  { label: 'WV', value: 'WV' },
  { label: 'WI', value: 'WI' },
  { label: 'WY', value: 'WY' }
]

const AddressInput = React.forwardRef(function AddressInput(
  { className, value = {}, onChange, label = '', helpText = '', errorMessage },
  _ref
) {
  const currentAddress = value || {}

  function updateField(name, fieldValue) {
    const newAddress = { ...currentAddress, [name]: fieldValue }
    onChange(newAddress)
  }

  return (
    <div className={className}>
      {label && (
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">{label}</h3>
          <div className="mt-2 w-16 border-t-2 border-purple-600 rounded-sm" />
        </div>
      )}
      {helpText && <p className="mb-6 text-sm text-gray-600">{helpText}</p>}
      {errorMessage && <p className="mt-1 mb-3 text-sm text-red-500">{errorMessage}</p>}

      <div className="space-y-4">
        <div>
          <Label htmlFor="address-line1">Address Line 1</Label>
          <Input
            id="address-line1"
            type="text"
            placeholder="123 Main Street"
            value={currentAddress.line1 || ''}
            onChange={(event) => updateField('line1', event.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="address-line2">Address Line 2</Label>
          <Input
            id="address-line2"
            type="text"
            placeholder="Apartment, suite, etc. (optional)"
            value={currentAddress.line2 || ''}
            onChange={(event) => updateField('line2', event.target.value)}
            className="mt-1"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              type="text"
              placeholder="City"
              value={currentAddress.city || ''}
              onChange={(event) => updateField('city', event.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <SelectInput
              id="state"
              label="State"
              options={STATES}
              value={currentAddress.state || ''}
              onChange={(event) => updateField('state', event.target.value)}
            />
          </div>
        </div>

        <div className="w-full sm:w-48">
          <Label htmlFor="postal-code">ZIP Code</Label>
          <Input
            id="postal-code"
            type="text"
            placeholder="12345"
            value={currentAddress.postCode || ''}
            onChange={(event) => updateField('postCode', event.target.value)}
            className="mt-1"
          />
        </div>
      </div>
    </div>
  )
})

AddressInput.displayName = 'AddressInput'

AddressInput.propTypes = {
  label: PropTypes.string,
  helpText: PropTypes.string,
  className: PropTypes.string,
  errorMessage: PropTypes.string,
  value: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postCode: PropTypes.string
  }),
  onChange: PropTypes.func
}

export default AddressInput
